/**
 * Widget renderer library. Each widget gets `data` (already filtered + shaped
 * by the data resolver layer) and `config` (display-only knobs). The renderer
 * is data-source-agnostic: it doesn't know what data source produced the rows,
 * only how to render them based on widget.type.
 */

import * as React from 'react';
import { type ReactNode, Suspense, lazy, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { metricColor, resolvePalette, useBrandTheme } from './brand-theme';
import { Card, ChartTip, DTable, KpiCard, SecTitle, Spinner } from './components';
import type {
  BarConfig,
  FormulaConfig,
  KpiConfig,
  MatrixConfig,
  TableConfig,
  TextConfig,
  Widget,
} from './dashboard-types';
import { evaluateFormula } from './formula';
import { METRIC_BY_KEY, type UnitClass } from './panel-schema/units';
import { type Theme, fmt, useTheme } from './theme';

/**
 * Metrics where DOWN is good. A falling CPL or CPC is an improvement, so the
 * comparison arrow must not paint it red — the one thing a period-over-period
 * figure has to get right is which direction is good news.
 */
const INVERTED_METRICS = new Set(['cpl', 'cpc', 'cost']);

const formatValue = (
  v: number | null | undefined,
  format: 'I' | '$' | 'P' | 'N' | 'text' | undefined,
): string => {
  if (v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v))) return '—';
  switch (format) {
    case '$':
      return fmt.$(v as number);
    case 'P':
      return fmt.P(v as number);
    case 'N':
      return fmt.N(v as number);
    case 'text':
      return String(v);
    default:
      return fmt.I(v as number);
  }
};

// ─── KPI ────────────────────────────────────────────────────────────────────

export function KpiWidget({
  widget,
  data,
}: {
  widget: Widget;
  data?: {
    value: number | null;
    trend?: number;
    sub?: string;
    /** Present only when the panel asked to compare against another period. */
    comparison?: { value: number | null; delta: number | null; label: string };
  };
}) {
  const cfg = widget.config as KpiConfig;
  const C = useTheme();
  const value = data?.value ?? null;
  const formatted = formatValue(value, cfg.format ?? 'I');
  let accent: string | undefined;
  if (cfg.threshold && value !== null) {
    const { good, bad, invert } = cfg.threshold;
    const isGood = invert ? value <= good : value >= good;
    const isBad = invert ? value >= bad : value <= bad;
    accent = isGood ? C.good : isBad ? C.bad : undefined;
  }
  return (
    <KpiCard
      label={widget.title ?? widget.id}
      value={formatted}
      sub={data?.sub}
      accent={accent}
      trend={cfg.showTrend !== false ? data?.trend : undefined}
      // The resolver produces a comparison whenever the panel asks for one.
      // KpiCard has been able to RENDER one all along — it just was never
      // handed it, so "compare against the previous period" resolved correctly
      // and showed nothing, which is the same shape of defect as a stored
      // setting no renderer reads.
      compareVal={
        data?.comparison ? formatValue(data.comparison.value ?? 0, cfg.format ?? 'I') : undefined
      }
      compareDelta={data?.comparison ? { pct: data.comparison.delta ?? null } : undefined}
      trendInvert={INVERTED_METRICS.has(widget.dataSource.metric ?? '')}
    />
  );
}

// ─── Bar (horizontal or vertical) ──────────────────────────────────────────

export function BarWidget({
  widget,
  data,
}: {
  widget: Widget;
  data?: { rows: { name: string; value: number; cpl?: number }[] };
}) {
  const cfg = widget.config as BarConfig;
  const C = useTheme();
  const brandTheme = useBrandTheme();
  const palette = resolvePalette(brandTheme);
  const cplThreshold = brandTheme.thresholds?.cpl;
  const horizontal = widget.type === 'bar_h';
  const rows = data?.rows ?? [];

  const colorFor = (row: { cpl?: number }, i: number): string => {
    if (cfg.colorBy === 'cpl' && row.cpl !== undefined)
      return metricColor(row.cpl, cplThreshold, C);
    return palette[i % palette.length] ?? C.accent;
  };

  return (
    <Card>
      <SecTitle>{widget.title ?? widget.id}</SecTitle>
      <ResponsiveContainer width="100%" height={Math.max(180, widget.h * 60 - 80)}>
        <BarChart
          data={rows}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 0, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={C.border}
            horizontal={!horizontal}
            vertical={horizontal}
          />
          {/*
            Both axes MUST be direct children of BarChart. Recharts finds its
            axes by inspecting chart children by type, and a React Fragment
            hides them: wrapping these in <>…</> left the chart with no
            detected axes at all, which rendered every horizontal bar chart as
            a single collapsed bar with no category labels. Keep them flat.
          */}
          <XAxis
            {...(horizontal
              ? {
                  type: 'number' as const,
                  tick: { fill: C.muted, fontSize: 11 },
                  tickFormatter: (v: number) => formatValue(Number(v), cfg.axisFormat ?? 'N'),
                }
              : {
                  dataKey: 'name',
                  tick: { fill: C.sub, fontSize: 11 },
                })}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            {...(horizontal
              ? {
                  dataKey: 'name',
                  type: 'category' as const,
                  tick: { fill: C.sub, fontSize: 11 },
                  width: 120,
                }
              : {
                  tick: { fill: C.muted, fontSize: 11 },
                  tickFormatter: (v: number) => formatValue(Number(v), cfg.axisFormat ?? 'N'),
                })}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={
              <ChartTip
                fmtVal={(_n, v) =>
                  formatValue(typeof v === 'number' ? v : Number(v), cfg.axisFormat ?? 'N')
                }
              />
            }
          />
          <Bar dataKey="value" radius={horizontal ? [0, 5, 5, 0] : [5, 5, 0, 0]}>
            {rows.map((r, i) => (
              <Cell key={r.name} fill={colorFor(r, i)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

// ─── Line chart ────────────────────────────────────────────────────────────

export function LineWidget({
  widget,
  data,
}: {
  widget: Widget;
  data?: {
    rows: { name: string }[];
    series: { key: string; label: string; axis?: 'left' | 'right' }[];
  };
}) {
  const cfg = widget.config as BarConfig;
  const C = useTheme();
  const palette = resolvePalette(useBrandTheme());
  const rows = data?.rows ?? [];
  const series = data?.series ?? [];
  const axisForSeries = (axis: 'left' | 'right' | undefined) =>
    axis === 'right' ? 'right' : 'left';
  const formatForUnit = (unit: UnitClass | undefined): 'I' | '$' | 'P' | 'N' => {
    switch (unit) {
      case 'currency':
        return '$';
      case 'percent':
        return 'P';
      default:
        return 'N';
    }
  };
  const unitForAxis = (axis: 'left' | 'right'): UnitClass | undefined =>
    series
      .filter((entry) => axisForSeries(entry.axis) === axis)
      .map((entry) => METRIC_BY_KEY[entry.key]?.unit)
      .find((unit): unit is UnitClass => Boolean(unit));
  const leftFormat = cfg.axisFormat ?? formatForUnit(unitForAxis('left'));
  const rightFormat = formatForUnit(unitForAxis('right'));
  const formatForSeries = (name: string) => {
    const entry = series.find((candidate) => candidate.label === name || candidate.key === name);
    return axisForSeries(entry?.axis) === 'right' ? rightFormat : leftFormat;
  };
  const hasRightAxis = series.some((entry) => axisForSeries(entry.axis) === 'right');

  return (
    <Card>
      <SecTitle>{widget.title ?? widget.id}</SecTitle>
      <ResponsiveContainer width="100%" height={Math.max(180, widget.h * 60 - 80)}>
        <LineChart
          data={rows}
          margin={{ top: 0, right: hasRightAxis ? 30 : 20, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
          <XAxis
            dataKey="name"
            tick={{ fill: C.sub, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: C.muted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => formatValue(Number(v), leftFormat)}
          />
          {hasRightAxis ? (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: C.muted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => formatValue(Number(v), rightFormat)}
            />
          ) : null}
          <Tooltip
            content={
              <ChartTip
                fmtVal={(name, v) =>
                  formatValue(typeof v === 'number' ? v : Number(v), formatForSeries(name))
                }
              />
            }
          />
          {series.length > 1 ? <Legend wrapperStyle={{ fontSize: 12, color: C.muted }} /> : null}
          {series.map((s, i) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              yAxisId={axisForSeries(s.axis)}
              stroke={palette[i % palette.length] ?? C.accent}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

// ─── Pie ────────────────────────────────────────────────────────────────────

export function PieWidget({
  widget,
  data,
}: {
  widget: Widget;
  data?: { rows: { name: string; value: number }[] };
}) {
  const C = useTheme();
  const palette = resolvePalette(useBrandTheme());
  const rows = data?.rows ?? [];
  return (
    <Card>
      <SecTitle>{widget.title ?? widget.id}</SecTitle>
      <ResponsiveContainer width="100%" height={Math.max(180, widget.h * 60 - 80)}>
        <PieChart>
          <Pie data={rows} dataKey="value" nameKey="name" outerRadius="75%">
            {rows.map((r, i) => (
              <Cell key={r.name} fill={palette[i % palette.length] ?? C.accent} />
            ))}
          </Pie>
          <Tooltip content={<ChartTip />} />
          <Legend wrapperStyle={{ fontSize: 12, color: C.muted }} />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  );
}

// ─── Table ──────────────────────────────────────────────────────────────────

/**
 * What a table shows before anyone configures its columns.
 *
 * These keys must exist on the rows the resolver emits for a table, which are
 * aggregated rows keyed { name, Cost, Impr, Clicks, Leads, CPL, CPC, CTR }.
 * The previous fallback asked for a `value` column that the resolver has never
 * produced, so dropping a Table panel on a dashboard rendered an em-dash in
 * every row until you configured columns by hand. Nothing failed; it just
 * quietly showed nothing.
 */
export const DEFAULT_TABLE_COLUMNS: NonNullable<TableConfig['columns']> = [
  { key: 'name', label: 'Name', format: 'text' },
  { key: 'Cost', label: 'Cost', format: '$' },
  { key: 'Clicks', label: 'Clicks', format: 'I' },
  { key: 'Leads', label: 'Leads', format: 'I' },
  { key: 'CPL', label: 'CPL', format: '$' },
];

export function TableWidget({
  widget,
  data,
}: {
  widget: Widget;
  data?: { rows: Record<string, unknown>[] };
}) {
  const cfg = widget.config as TableConfig;
  const cols = cfg.columns ?? DEFAULT_TABLE_COLUMNS;
  const rows = data?.rows ?? [];
  return (
    <Card>
      <SecTitle>{widget.title ?? widget.id}</SecTitle>
      <DTable
        cols={cols.map((c) => ({
          k: c.key,
          l: c.label,
          r: c.format !== 'text' && c.key !== 'name',
        }))}
        rows={rows}
        cellFn={(row, col) => {
          const v = (row as Record<string, unknown>)[col.k];
          const colCfg = cols.find((cc) => cc.key === col.k);
          if (typeof v === 'number') return formatValue(v, colCfg?.format ?? 'I');
          return v === null || v === undefined ? '—' : String(v);
        }}
      />
    </Card>
  );
}

// ─── Matrix (heatmap) ──────────────────────────────────────────────────────

export function MatrixWidget({
  widget,
  data,
}: {
  widget: Widget;
  data?: {
    rows: string[];
    cols: string[];
    cells: { rowIdx: number; colIdx: number; value: number; cpl?: number }[];
  };
}) {
  const cfg = widget.config as MatrixConfig;
  const C = useTheme();
  const brandTheme = useBrandTheme();
  const rows = data?.rows ?? [];
  const cols = data?.cols ?? [];
  const cells = data?.cells ?? [];
  const cellLookup = new Map<string, { value: number; cpl?: number }>();
  for (const c of cells) cellLookup.set(`${c.rowIdx}|${c.colIdx}`, c);

  return (
    <Card>
      <SecTitle>{widget.title ?? widget.id}</SecTitle>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th
                style={{
                  position: 'sticky',
                  left: 0,
                  background: C.card,
                  padding: '8px 10px',
                  textAlign: 'left',
                  color: C.muted,
                  fontWeight: 600,
                }}
              >
                {' '}
              </th>
              {cols.map((c) => (
                <th
                  key={c}
                  style={{
                    padding: '8px 10px',
                    textAlign: 'right',
                    color: C.muted,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((rowName, ri) => (
              <tr key={rowName}>
                <th
                  style={{
                    position: 'sticky',
                    left: 0,
                    background: C.card,
                    padding: '8px 10px',
                    textAlign: 'left',
                    color: C.text,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {rowName}
                </th>
                {cols.map((colName, ci) => {
                  const cell = cellLookup.get(`${ri}|${ci}`);
                  if (!cell) {
                    return (
                      <td
                        key={colName}
                        style={{ padding: '8px 10px', textAlign: 'right', color: C.muted }}
                      >
                        —
                      </td>
                    );
                  }
                  const colorMetric =
                    cfg.cellMetric === 'cpl' && cell.cpl !== undefined ? cell.cpl : cell.value;
                  let bg: string;
                  if (cfg.cellMetric === 'cpl' && cfg.colorBy === 'column-relative') {
                    // Column-relative coloring: ±15% of the column's CPL median
                    // becomes a green/yellow/red band. Mirrors the standalone MG
                    // matrix exactly.
                    const colCpls = cells
                      .filter((x) => x.colIdx === ci && (x.cpl ?? 0) > 0)
                      .map((x) => x.cpl as number)
                      .sort((a, b) => a - b);
                    if (colCpls.length < 2 || cell.cpl == null) {
                      bg = C.surfaceHigh;
                    } else {
                      const median = colCpls[Math.floor(colCpls.length / 2)] ?? 0;
                      const good = median * 0.85;
                      const bad = median * 1.15;
                      const cpl = cell.cpl;
                      bg = cpl <= good ? `${C.good}40` : cpl >= bad ? `${C.bad}40` : `${C.warn}40`;
                    }
                  } else {
                    bg = metricColor(
                      colorMetric,
                      cfg.cellMetric ? brandTheme.thresholds?.[cfg.cellMetric] : undefined,
                      C,
                    );
                  }
                  return (
                    <td
                      key={colName}
                      style={{
                        padding: '8px 10px',
                        textAlign: 'right',
                        background: bg,
                        color: C.text,
                        fontFamily: 'var(--font-mono, monospace)',
                      }}
                    >
                      {formatValue(cell.value, cfg.cellFormat ?? 'I')}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Map (geographic distribution by postcode prefix / region) ────────────

export interface MapPayload {
  rows: { name: string; value: number; subtitle?: string }[];
  total?: number;
}

/** Color interpolator matching MG's two-stop scheme (cool blue → hot red). */
function defaultHeatColor(t: number): string {
  if (t <= 0) return 'rgb(76,158,255)';
  if (t < 0.5) {
    const s = t * 2;
    const r = Math.round(76 + s * (240 - 76));
    const g = Math.round(158 + s * (165 - 158));
    const b = Math.round(255 + s * (0 - 255));
    return `rgb(${r},${g},${b})`;
  }
  const s = (t - 0.5) * 2;
  const r = Math.round(240 + s * (230 - 240));
  const g = Math.round(165 + s * (51 - 165));
  const b = Math.round(0 + s * (41 - 0));
  return `rgb(${r},${g},${b})`;
}

export function MapWidget({
  widget,
  data,
}: {
  widget: Widget;
  data?: MapPayload;
}) {
  const cfg = (widget.config ?? {}) as import('./dashboard-types').MapConfig;
  const hasGeo = Boolean(cfg.topojsonUrl);
  if (hasGeo) {
    return <GeoMapWidget widget={widget} data={data} cfg={cfg} />;
  }
  return <ListMapWidget widget={widget} data={data} />;
}

/** Fallback list rendering — used when no topojsonUrl is configured. */
function ListMapWidget({
  widget,
  data,
}: {
  widget: Widget;
  data?: MapPayload;
}) {
  const C = useTheme();
  const rows = data?.rows ?? [];
  const total = data?.total ?? rows.reduce((s, r) => s + r.value, 0);
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0) || 1;
  return (
    <Card>
      <SecTitle>{widget.title ?? 'Geographic distribution'}</SecTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
        {rows.length === 0 ? (
          <p style={{ color: C.muted, fontSize: 12 }}>No location data yet.</p>
        ) : null}
        {rows.map((r) => {
          const ratio = r.value / max;
          const pct = total > 0 ? (r.value / total) * 100 : 0;
          return (
            <div
              key={r.name}
              style={{
                display: 'grid',
                gridTemplateColumns: '110px 1fr 70px',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <span
                style={{ color: C.text, fontSize: 12, fontFamily: 'var(--font-mono, monospace)' }}
              >
                {r.name}
              </span>
              <div
                style={{
                  height: 6,
                  background: C.surfaceHigh,
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <div style={{ height: '100%', width: `${ratio * 100}%`, background: C.accent }} />
              </div>
              <span
                style={{
                  color: C.muted,
                  fontSize: 11,
                  fontFamily: 'var(--font-mono, monospace)',
                  textAlign: 'right',
                }}
              >
                {fmt.I(r.value)} · {pct.toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/** TopoJSON-backed heatmap. Lazy-loads react-simple-maps so the dashboard
 *  bundle doesn't pay for it on tabs that don't use a map. The dep lives in
 *  apps/app (the only consumer of this package today); the dynamic import is
 *  cast to any so packages/ui doesn't need a direct devDep on it. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ComposableMap = lazy(() =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (import('react-simple-maps' as any) as Promise<any>).then((m: any) => ({
    default: m.ComposableMap,
  })),
) as unknown as React.ComponentType<{
  projection?: string;
  projectionConfig?: { center?: [number, number]; scale?: number };
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  children?: ReactNode;
}>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Geographies = lazy(() =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (import('react-simple-maps' as any) as Promise<any>).then((m: any) => ({
    default: m.Geographies,
  })),
) as unknown as React.ComponentType<{
  geography?: string;
  children?: (args: {
    geographies: Array<{ rsmKey: string; properties: Record<string, unknown> }>;
  }) => ReactNode;
}>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Geography = lazy(() =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (import('react-simple-maps' as any) as Promise<any>).then((m: any) => ({
    default: m.Geography,
  })),
) as unknown as React.ComponentType<{
  geography?: { rsmKey: string; properties: Record<string, unknown> };
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  style?: {
    default?: React.CSSProperties;
    hover?: React.CSSProperties;
    pressed?: React.CSSProperties;
  };
}>;

/** Tiny error boundary to catch lazy-import failures or render errors inside
 *  the GeoMap subtree. Falls back to a friendly retry hint instead of a
 *  white-screen crash that takes the whole dashboard down. */
class MapErrorBoundary extends React.Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  override componentDidCatch(err: Error) {
    console.error('[GeoMapWidget] render error', err);
  }
  override render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

function GeoMapWidget({
  widget,
  data,
  cfg,
}: {
  widget: Widget;
  data?: MapPayload;
  cfg: import('./dashboard-types').MapConfig;
}) {
  const C = useTheme();
  const [hoverRegion, setHoverRegion] = useState<{
    key: string;
    value: number;
  } | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [renderedAt, setRenderedAt] = useState<number | null>(null);

  // 10-second timeout — if Geographies never paints (CDN slow / down), surface
  // an error message so the user knows to retry instead of staring at "Loading
  // map…" forever.
  React.useEffect(() => {
    if (renderedAt) return;
    const id = window.setTimeout(() => {
      if (!renderedAt) setTimedOut(true);
    }, 10_000);
    return () => window.clearTimeout(id);
  }, [renderedAt]);

  const rows = data?.rows ?? [];
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0) || 1;
  const total = data?.total ?? rows.reduce((s, r) => s + r.value, 0);

  // Build a key→value lookup for fast paint. Optionally slice the row name to
  // the first `keyPrefix` chars to match the geometry's region code length.
  const valueByKey = useMemo(() => {
    const m: Record<string, number> = {};
    const slice = cfg.keyPrefix && cfg.keyPrefix > 0;
    for (const r of rows) {
      const k = slice ? r.name.slice(0, cfg.keyPrefix!) : r.name;
      m[k] = (m[k] ?? 0) + r.value;
    }
    return m;
  }, [rows, cfg.keyPrefix]);

  const regionKeyField = cfg.regionKeyField ?? 'pc4';
  const center: [number, number] =
    cfg.centerLng != null && cfg.centerLat != null
      ? [cfg.centerLng, cfg.centerLat]
      : (cfg.center ?? [5.3, 52.2]); // NL default
  const scale = cfg.scale ?? 6500;

  const errorFallback = (
    <div style={{ padding: '40px 16px', textAlign: 'center', color: C.muted, fontSize: 13 }}>
      <p style={{ marginBottom: 8 }}>Map data unavailable.</p>
      <p style={{ fontSize: 11 }}>
        The TopoJSON source may be slow or unreachable. Refresh the page to retry.
      </p>
    </div>
  );

  return (
    <Card>
      <SecTitle>{widget.title ?? 'Geographic distribution'}</SecTitle>
      <div style={{ position: 'relative', height: 360, marginTop: 8 }}>
        {timedOut ? (
          errorFallback
        ) : (
          <MapErrorBoundary fallback={errorFallback}>
            <Suspense fallback={<p style={{ color: C.muted, fontSize: 12 }}>Loading map…</p>}>
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ center, scale }}
                width={800}
                height={360}
                style={{ width: '100%', height: '100%' }}
              >
                <Geographies geography={cfg.topojsonUrl}>
                  {({
                    geographies,
                  }: {
                    geographies: Array<{ rsmKey: string; properties: Record<string, unknown> }>;
                  }) => {
                    if (geographies.length > 0 && !renderedAt) setRenderedAt(Date.now());
                    return geographies.map((geo) => {
                      const key = String(geo.properties?.[regionKeyField] ?? '');
                      const value = valueByKey[key] ?? 0;
                      const t = value > 0 ? Math.sqrt(value / max) : 0;
                      const fill = value > 0 ? defaultHeatColor(t) : C.surfaceHigh;
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          onMouseEnter={() =>
                            value > 0 ? setHoverRegion({ key, value }) : setHoverRegion(null)
                          }
                          onMouseLeave={() => setHoverRegion(null)}
                          style={{
                            default: { fill, stroke: C.border, strokeWidth: 0.3, outline: 'none' },
                            hover: { fill, stroke: C.accent, strokeWidth: 0.8, outline: 'none' },
                            pressed: { fill, outline: 'none' },
                          }}
                        />
                      );
                    });
                  }}
                </Geographies>
              </ComposableMap>
            </Suspense>
          </MapErrorBoundary>
        )}
        {hoverRegion ? (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: C.surfaceHigh,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              padding: '6px 10px',
              fontSize: 12,
              color: C.text,
            }}
          >
            <strong>{hoverRegion.key}</strong> · {fmt.I(hoverRegion.value)}
            {total > 0 ? (
              <span style={{ color: C.muted }}>
                {' '}
                · {((hoverRegion.value / total) * 100).toFixed(0)}%
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 8,
          fontSize: 10,
          color: C.muted,
        }}
      >
        <span>
          {rows.length} regions · {fmt.I(total)} total
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              display: 'inline-block',
              width: 36,
              height: 6,
              background: 'linear-gradient(90deg, rgb(76,158,255), rgb(240,165,0), rgb(230,51,41))',
              borderRadius: 3,
            }}
          />
          low → high
        </span>
      </div>
    </Card>
  );
}

// ─── Funnel (Impressions → Clicks → Leads with drop-rates) ────────────────

export interface FunnelPayload {
  impressions: number;
  clicks: number;
  leads: number;
}

export function FunnelWidget({
  widget,
  data,
}: {
  widget: Widget;
  data?: FunnelPayload;
}) {
  const C = useTheme();
  const palette = resolvePalette(useBrandTheme());
  const impr = data?.impressions ?? 0;
  const clicks = data?.clicks ?? 0;
  const leads = data?.leads ?? 0;
  const ctr = impr > 0 ? (clicks / impr) * 100 : 0;
  const cvr = clicks > 0 ? (leads / clicks) * 100 : 0;

  const steps = [
    {
      label: 'Impressions',
      value: impr,
      pct: 100,
      color: palette[3] ?? '#4c9eff',
      sub: null as string | null,
    },
    {
      label: 'Clicks',
      value: clicks,
      pct: impr > 0 ? (clicks / impr) * 100 : 0,
      color: palette[2] ?? '#27b870',
      sub: `CTR ${ctr.toFixed(2)}%`,
    },
    {
      label: 'Leads',
      value: leads,
      pct: clicks > 0 ? (leads / clicks) * 100 : 0,
      color: C.accent,
      sub: `CVR ${cvr.toFixed(2)}%`,
    },
  ];

  const maxW = 92;
  const minW = 38;

  return (
    <Card>
      <SecTitle>{widget.title ?? 'Funnel'}</SecTitle>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
          padding: '8px 0',
        }}
      >
        {steps.map((s, i) => {
          const w = maxW - (i * (maxW - minW)) / (steps.length - 1);
          return (
            <div
              key={s.label}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  width: `${w}%`,
                  background: s.color,
                  borderRadius:
                    i === 0
                      ? '10px 10px 4px 4px'
                      : i === steps.length - 1
                        ? '4px 4px 16px 16px'
                        : '4px',
                  padding: '14px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  {s.label}
                </span>
                <span
                  style={{
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 800,
                    fontFamily: 'var(--font-mono, monospace)',
                  }}
                >
                  {fmt.I(s.value)}
                </span>
              </div>
              {i < steps.length - 1 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '4px 0',
                  }}
                >
                  <div style={{ width: 1, height: 8, background: C.border }} />
                  <span
                    style={{ color: steps[i + 1]?.color ?? C.muted, fontSize: 11, fontWeight: 700 }}
                  >
                    ▼ {steps[i + 1]?.sub}
                  </span>
                  <div style={{ width: 1, height: 8, background: C.border }} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Pacing (composite: budget vs spend, leads vs target, projected EOM) ──

export interface PacingPayload {
  month: string;
  totalBudget: number;
  totalLeadsTarget: number;
  mtdSpend: number;
  mtdLeads: number;
  daysElapsed: number;
  daysInMonth: number;
  rows?: { label: string; budget: number; leadsTarget: number }[];
}

export function PacingWidget({
  widget,
  data,
}: {
  widget: Widget;
  data?: PacingPayload;
}) {
  const C = useTheme();
  if (!data) {
    return (
      <Card>
        <SecTitle>{widget.title ?? 'Pacing'}</SecTitle>
        <p style={{ color: C.muted, fontSize: 12, padding: '24px 8px', textAlign: 'center' }}>
          No pacing data for this period yet.
          <br />
          <span style={{ fontSize: 11 }}>
            An admin can set monthly budgets and lead targets at <code>/admin/pacing</code>.
          </span>
        </p>
      </Card>
    );
  }
  // Targets table empty + no actuals → same "set up pacing" copy.
  if (
    !data.totalBudget &&
    !data.totalLeadsTarget &&
    !data.mtdSpend &&
    !data.mtdLeads &&
    (!data.rows || data.rows.length === 0)
  ) {
    return (
      <Card>
        <SecTitle>{widget.title ?? 'Pacing'}</SecTitle>
        <p style={{ color: C.muted, fontSize: 12, padding: '24px 8px', textAlign: 'center' }}>
          No targets configured for {data.month || 'this period'}.
          <br />
          <span style={{ fontSize: 11 }}>
            An admin can add them at <code>/admin/pacing</code>, or clone from a previous month.
          </span>
        </p>
      </Card>
    );
  }
  const { totalBudget, totalLeadsTarget, mtdSpend, mtdLeads, daysElapsed, daysInMonth, month } =
    data;
  const dayRatio = daysInMonth > 0 ? daysElapsed / daysInMonth : 0;
  const expectedSpend = totalBudget * dayRatio;
  const expectedLeads = totalLeadsTarget * dayRatio;
  const projectedSpend = daysElapsed > 0 ? (mtdSpend / daysElapsed) * daysInMonth : 0;
  const projectedLeads = daysElapsed > 0 ? (mtdLeads / daysElapsed) * daysInMonth : 0;
  const spendPace = totalBudget > 0 ? mtdSpend / totalBudget : 0;
  const leadsPace = totalLeadsTarget > 0 ? mtdLeads / totalLeadsTarget : 0;

  const spendDelta = mtdSpend - expectedSpend;
  const leadsDelta = mtdLeads - expectedLeads;

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <SecTitle>{widget.title ?? 'Pacing'}</SecTitle>
        <span style={{ color: C.muted, fontSize: 11, fontFamily: 'var(--font-mono, monospace)' }}>
          {month} · day {daysElapsed}/{daysInMonth}
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginTop: 12,
        }}
      >
        <PacingTrack
          C={C}
          label="Spend"
          actual={mtdSpend}
          target={totalBudget}
          pace={spendPace}
          dayRatio={dayRatio}
          projected={projectedSpend}
          delta={spendDelta}
          format="$"
          invert={true}
        />
        <PacingTrack
          C={C}
          label="Leads"
          actual={mtdLeads}
          target={totalLeadsTarget}
          pace={leadsPace}
          dayRatio={dayRatio}
          projected={projectedLeads}
          delta={leadsDelta}
          format="I"
          invert={false}
        />
      </div>
    </Card>
  );
}

function PacingTrack({
  C,
  label,
  actual,
  target,
  pace,
  dayRatio,
  projected,
  delta,
  format,
  invert,
}: {
  C: Theme;
  label: string;
  actual: number;
  target: number;
  pace: number;
  dayRatio: number;
  projected: number;
  delta: number;
  format: 'I' | '$' | 'P' | 'N';
  invert: boolean;
}) {
  const pct = Math.min(1, Math.max(0, pace));
  const expectedPct = Math.min(1, Math.max(0, dayRatio));
  // For spend (invert=true), under-spending = good (green). For leads, over = good.
  const onTrack = invert ? pace <= dayRatio + 0.05 : pace >= dayRatio - 0.05;
  const accent = onTrack ? C.good : C.gold;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span
          style={{ color: C.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {label}
        </span>
        <span style={{ color: accent, fontSize: 11, fontFamily: 'var(--font-mono, monospace)' }}>
          {(pace * 100).toFixed(0)}% of plan
        </span>
      </div>
      <div style={{ fontSize: 22, color: C.text, fontWeight: 500 }}>
        {formatValue(actual, format)}
        <span style={{ color: C.muted, fontSize: 13, marginLeft: 6 }}>
          / {formatValue(target, format)}
        </span>
      </div>
      <div
        style={{
          position: 'relative',
          height: 6,
          background: C.surfaceHigh,
          borderRadius: 3,
          marginTop: 8,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            width: `${pct * 100}%`,
            background: accent,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: -2,
            bottom: -2,
            left: `${expectedPct * 100}%`,
            width: 2,
            background: C.text,
            opacity: 0.6,
          }}
          title="expected pacing"
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <span style={{ color: C.muted, fontSize: 11 }}>
          Projected: {formatValue(projected, format)}
        </span>
        <span
          style={{
            color: (invert ? delta < 0 : delta > 0) ? C.good : C.gold,
            fontSize: 11,
            fontFamily: 'var(--font-mono, monospace)',
          }}
        >
          {delta >= 0 ? '+' : ''}
          {formatValue(delta, format)} vs plan
        </span>
      </div>
    </div>
  );
}

// ─── Text (markdown-flavored) ──────────────────────────────────────────────

export function TextWidget({ widget }: { widget: Widget }) {
  const cfg = widget.config as TextConfig;
  return (
    <Card>
      {widget.title ? <SecTitle>{widget.title}</SecTitle> : null}
      <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{cfg.markdown ?? ''}</div>
    </Card>
  );
}

// ─── Formula (calculated metric) ───────────────────────────────────────────

/**
 * Resolves a formula against the same payload shape KPI widgets read from.
 * Variables are case-insensitive against the payload's `totals` keys plus
 * any top-level numeric fields (so {budget} works when the snapshot
 * carries one). Missing variables → NaN → renders as "—" via formatValue.
 */
export function FormulaWidget({
  widget,
  data,
}: {
  widget: Widget;
  data?: { totals?: Record<string, number>; [k: string]: unknown };
}) {
  const cfg = widget.config as FormulaConfig;
  const expr = cfg.formula ?? '';
  const vars: Record<string, number> = {};
  const totals = (data?.totals ?? {}) as Record<string, number>;
  for (const [k, v] of Object.entries(totals)) {
    if (typeof v === 'number') {
      vars[k] = v;
      vars[k.toLowerCase()] = v;
    }
  }
  for (const [k, v] of Object.entries(data ?? {})) {
    if (typeof v === 'number') {
      vars[k] = v;
      vars[k.toLowerCase()] = v;
    }
  }

  const result = expr ? evaluateFormula(expr, vars) : Number.NaN;
  const formatted = formatValue(Number.isFinite(result) ? result : null, cfg.format ?? 'N');

  let toneColor: string | undefined;
  if (cfg.threshold && Number.isFinite(result)) {
    const { good, bad, invert } = cfg.threshold;
    const isGood = invert ? result <= good : result >= good;
    const isBad = invert ? result >= bad : result <= bad;
    toneColor = isGood ? 'var(--success)' : isBad ? 'var(--danger)' : undefined;
  }

  return (
    <KpiCard
      label={widget.title ?? widget.id}
      value={formatted}
      sub={expr || 'No formula set'}
      accent={toneColor}
    />
  );
}

// ─── Dispatcher ────────────────────────────────────────────────────────────

export interface WidgetData {
  ready: boolean;
  loading?: boolean;
  /**
   * A SENTENCE FOR THE READER, never a code.
   *
   * `WidgetRenderer` prints this verbatim onto a client's dashboard and onto
   * the public embed, so whatever a host puts here is what a paying customer
   * reads. It held `unsupported_widget_type:map` until 2026-09-06.
   *
   * The obligation is on the host, deliberately. This package is shared and
   * cannot reach into an app to translate a code, and the strings that reach
   * this field are composed by the host's data layer rather than sent by the
   * API -- so there is nothing here to look up. Whoever writes the string
   * writes it in words.
   */
  error?: string | null;
  payload?: unknown;
}

export function WidgetRenderer({ widget, data }: { widget: Widget; data?: WidgetData }) {
  if (data?.error)
    return (
      <Card>
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>⚠️ {data.error}</p>
      </Card>
    );
  if (data?.loading || !data?.ready) {
    return (
      <Card>
        <Spinner msg="" />
      </Card>
    );
  }
  // Resolver-emitted empty state: data.payload?.emptyMessage carries explicit
  // copy for "leads not configured", "no pacing yet", etc. Shown here so we
  // don't have to teach every widget about every empty case.
  const emptyMessage =
    data &&
    typeof data.payload === 'object' &&
    data.payload !== null &&
    'emptyMessage' in data.payload
      ? (data.payload as { emptyMessage?: unknown }).emptyMessage
      : undefined;
  const emptyHelpUrl =
    data &&
    typeof data.payload === 'object' &&
    data.payload !== null &&
    'emptyHelpUrl' in data.payload
      ? (data.payload as { emptyHelpUrl?: unknown }).emptyHelpUrl
      : undefined;
  if (typeof emptyMessage === 'string' && emptyMessage) {
    return (
      <Card>
        <SecTitle>{widget.title ?? widget.id}</SecTitle>
        <p
          style={{ color: 'var(--muted)', fontSize: 13, padding: '24px 8px', textAlign: 'center' }}
        >
          {emptyMessage}
          {typeof emptyHelpUrl === 'string' && emptyHelpUrl ? (
            <>
              {' '}
              <a
                href={emptyHelpUrl}
                style={{
                  color: 'var(--brand)',
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                }}
              >
                Read the guide →
              </a>
            </>
          ) : null}
        </p>
      </Card>
    );
  }
  switch (widget.type) {
    case 'kpi':
      return <KpiWidget widget={widget} data={data.payload as never} />;
    case 'bar_h':
    case 'bar_v':
      return <BarWidget widget={widget} data={data.payload as never} />;
    case 'line':
      return <LineWidget widget={widget} data={data.payload as never} />;
    case 'pie':
      return <PieWidget widget={widget} data={data.payload as never} />;
    case 'table':
      return <TableWidget widget={widget} data={data.payload as never} />;
    case 'matrix':
      return <MatrixWidget widget={widget} data={data.payload as never} />;
    case 'pacing':
      return <PacingWidget widget={widget} data={data.payload as never} />;
    case 'map':
      return <MapWidget widget={widget} data={data.payload as never} />;
    case 'funnel':
      return <FunnelWidget widget={widget} data={data.payload as never} />;
    case 'text':
      return <TextWidget widget={widget} />;
    case 'formula':
      return <FormulaWidget widget={widget} data={data.payload as never} />;
    default:
      return (
        <Card>
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>
            Widget type "{widget.type}" not yet implemented in this renderer.
          </p>
        </Card>
      );
  }
}

/** Catalog of available widget types — drives the builder palette. */
export interface WidgetSpec {
  type: import('./dashboard-types').WidgetType;
  label: string;
  defaultSize: { w: number; h: number };
  description: string;
}

export const WIDGET_CATALOG: WidgetSpec[] = [
  {
    type: 'kpi',
    label: 'KPI',
    defaultSize: { w: 3, h: 2 },
    description: 'Single metric with optional trend',
  },
  {
    type: 'bar_h',
    label: 'Bar (horizontal)',
    defaultSize: { w: 6, h: 4 },
    description: 'Top-N by metric',
  },
  {
    type: 'bar_v',
    label: 'Bar (vertical)',
    defaultSize: { w: 6, h: 4 },
    description: 'Categorical comparison',
  },
  { type: 'line', label: 'Line', defaultSize: { w: 12, h: 4 }, description: 'Time series trend' },
  { type: 'pie', label: 'Pie', defaultSize: { w: 4, h: 4 }, description: 'Categorical breakdown' },
  { type: 'table', label: 'Table', defaultSize: { w: 6, h: 4 }, description: 'Sortable rows' },
  { type: 'matrix', label: 'Matrix', defaultSize: { w: 12, h: 5 }, description: 'Heatmap grid' },
  {
    type: 'funnel',
    label: 'Funnel',
    defaultSize: { w: 4, h: 5 },
    description: 'Impressions → Clicks → Leads with drop-rates',
  },
  {
    type: 'pacing',
    label: 'Pacing',
    defaultSize: { w: 12, h: 4 },
    description: 'Budget + leads vs plan',
  },
  {
    type: 'map',
    label: 'Map / postcode',
    defaultSize: { w: 6, h: 5 },
    description: 'Leads by postcode region',
  },
  { type: 'text', label: 'Text', defaultSize: { w: 6, h: 2 }, description: 'Static markdown' },
  {
    type: 'formula',
    label: 'Calculated metric',
    defaultSize: { w: 3, h: 2 },
    description: 'Formula: e.g. {Cost} / {Leads}',
  },
];

export type { Theme };
